import { CodedProperty, NamedProperty, ResolutionProperty } from "sprint-common-web";

import {
    resolveOrderContentTitle,
    resolveOrderContentVersion
} from "../../../common/api/orders-api/order/ContentReference";
import { OrderListItemResponse } from "../../../common/api/orders-api/order/GetOrderListResponse";
import { DeliveryEndpointType } from "../../../common/types/DeliveryEndpointType";
import { OrderAssetAvailability } from "../../common/types/OrderAssetAvailability";
import { OrderAssetStorageType } from "../../common/types/OrderAssetStorageType";
import { OrderResolutionDimensions } from "../../common/types/OrderResolutionDimensions";
import { OrderStatus } from "../../common/types/OrderStatus";
import { calculateOrderProgress, OrderProgressData } from "../../common/utils/calculateOrderProgress";

export interface OrderListingItem {
    id: string;
    content: string;
    video: CloudAssetsVideoListingItem;
    soundtracks: CloudAssetsSoundtrackListingItem[];
    subtitles: CloudAssetsSubtitleListingItem[];
    version: string;
    airline: CodedProperty;
    system: NamedProperty;
    dueDate: string;
    source: CloudOrderSource;
    deliveryDate?: string;
    deliveryEndpointName: string;
    deliveryEndpointId: string;
    deliveryEndpointType: DeliveryEndpointType;
    deliveryFolder?: string;
    isImported?: boolean;
    status: OrderStatus;
    startDate: string;
    createdDate: string;
    encodeSpecification: NamedProperty & { targetAspectRatio: NamedProperty };
    targetResolution?: OrderResolutionDimensions;
    pONumber: string;
    targetFilename: string;
    isModified?: boolean;
    isPinned?: boolean;
    processingExecutionArn?: string;
    deliveryExecutionArn?: string;
    buzzId?: string;
    statusDetails?: string;
    isTrailer?: boolean;
    isRevised?: boolean;
    isWatermark?: boolean;
    updatedDate?: string;
    runtime?: string;
    targetFilenames: string[];
    requestedSubTypes?: number[];
    progress: OrderProgressData;
}

interface CloudAssetsVideoListingItem {
    targetFilename: string;
    availability: OrderAssetAvailability;
    targetResolution?: ResolutionProperty;
    sourceResolution?: ResolutionProperty;
    nonMatchingVideos: CloudAssetsNonMatchingVideoListingItem[];
    storageType?: OrderAssetStorageType;
}

interface CloudAssetsNonMatchingVideoListingItem {
    aspectRatio: NamedProperty;
    resolution: ResolutionProperty;
    subtitle: CodedProperty;
    assetId: string;
}

interface CloudAssetsSoundtrackListingItem {
    language: CodedProperty;
    availability: OrderAssetAvailability;
    storageType?: OrderAssetStorageType;
}

interface CloudAssetsSubtitleListingItem {
    language: CodedProperty;
    availability: OrderAssetAvailability;
    isEmbedded: boolean;
    targetFilename?: string;
    storageType?: OrderAssetStorageType;
}

export enum CloudOrderSource {
    Shreq = 1,
    Buzz = 2,
    Sprint = 3
}

export const mapToOrderListingItem = (response: OrderListItemResponse, pinnedItems: string[]): OrderListingItem => {
    return {
        id: response.id,
        content: resolveOrderContentTitle(response.content),
        version: resolveOrderContentVersion(response.content),
        airline: response.airline,
        system: response.system,
        dueDate: response.deliveryInfo.dueDate,
        deliveryDate: response.deliveryInfo.deliveryDate,
        deliveryEndpointName: response.deliveryInfo.deliveryEndpoint.name,
        deliveryEndpointId: response.deliveryInfo.deliveryEndpoint.id,
        deliveryEndpointType: response.deliveryInfo.type,
        deliveryFolder: response.deliveryInfo.deliveryFolder,
        encodeSpecification: response.encodeSpecification,
        targetResolution: response.targetResolution,
        isImported: response.isImported,
        status: response.status,
        startDate: response.startDate,
        createdDate: response.audit.createdDate,
        video: {
            targetFilename: response.cloudAssets.video.targetFilename,
            availability: response.cloudAssets.video.availability,
            targetResolution: response.cloudAssets.video.targetResolution,
            sourceResolution: response.cloudAssets.video.sourceResolution,
            nonMatchingVideos: response.cloudAssets.video.nonMatchingVideos.map((video) => ({
                aspectRatio: video.aspectRatio,
                resolution: video.resolution,
                subtitle: video.subtitle,
                assetId: video.assetId
            })),
            storageType: response.cloudAssets.video.storageType
        },
        soundtracks: response.cloudAssets.soundtracks.map((soundtrack) => ({
            availability: soundtrack.availability,
            language: soundtrack.language,
            storageType: soundtrack.storageType
        })),
        subtitles: response.cloudAssets.subtitles.map((subtitle) => ({
            availability: subtitle.availability,
            language: subtitle.language,
            isEmbedded: subtitle.isEmbedded,
            targetFilename: subtitle.targetFilename,
            storageType: subtitle.storageType
        })),
        pONumber: response.pONumber,
        targetFilename: response.filename,
        processingExecutionArn: undefined,
        deliveryExecutionArn: undefined,
        buzzId: response.buzzId,
        statusDetails: response.statusDetails,
        isRevised: response.isRevised,
        isWatermark: response.isWatermark,
        isTrailer: response.isTrailer,
        updatedDate: response.audit.updatedDate,
        source: response.source,
        isPinned: pinnedItems.includes(response.id),
        runtime: response.runtime,
        targetFilenames: response.targetFilenames,
        requestedSubTypes: response.requestedSubTypes,
        progress: calculateOrderProgress(
            response.cloudAssets.video.availability,
            response.cloudAssets.soundtracks.map((s) => s.availability),
            response.cloudAssets.subtitles.map((s) => s.availability)
        )
    };
};

export const isDeliveryFolderMissingWhenRequired = (item: OrderListingItem): boolean => {
    return isDeliveryFolderRequired(item.deliveryEndpointType) && !item.deliveryFolder;
};

const isDeliveryFolderRequired = (deliveryEndpointType: DeliveryEndpointType) => {
    return deliveryEndpointType === DeliveryEndpointType.Aspera;
};
